// =============================================================================
// CodeInput - Reusable 6-digit OTP Input Component
// =============================================================================
// Extracted from VerifyPage for reuse in Gatekeeper share-link signup flow.
// Features: auto-focus, auto-advance, backspace nav, paste, auto-submit.
// =============================================================================

import { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';

export interface CodeInputHandle {
  reset: () => void;
  focus: () => void;
}

interface CodeInputProps {
  length?: number;
  onComplete: (code: string) => void;
  isLoading?: boolean;
  error?: string | null;
  attemptsRemaining?: number | null;
  autoFocus?: boolean;
}

export const CodeInput = forwardRef<CodeInputHandle, CodeInputProps>(function CodeInput(
  { length = 6, onComplete, isLoading = false, error = null, attemptsRemaining = null, autoFocus = true },
  ref
) {
  const [digits, setDigits] = useState<string[]>(Array(length).fill(''));
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  // Expose reset and focus methods via ref
  useImperativeHandle(ref, () => ({
    reset() {
      setDigits(Array(length).fill(''));
      setTimeout(() => inputRefs.current[0]?.focus(), 50);
    },
    focus() {
      inputRefs.current[0]?.focus();
    },
  }), [length]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, [autoFocus]);

  // Auto-submit when code is complete
  useEffect(() => {
    const code = digits.join('');
    if (code.length === length && digits.every((d) => d !== '')) {
      onCompleteRef.current(code);
    }
  }, [digits, length]);

  const handleDigitChange = useCallback((index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);

    setDigits(prev => {
      const next = [...prev];
      next[index] = digit;
      return next;
    });

    if (digit && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [length]);

  const handleKeyDown = useCallback((index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      setDigits(prev => {
        const next = [...prev];
        if (prev[index] === '' && index > 0) {
          next[index - 1] = '';
          inputRefs.current[index - 1]?.focus();
        } else {
          next[index] = '';
        }
        return next;
      });
    }

    if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1]?.focus();
    }
  }, [length]);

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, length);

    if (pastedData) {
      const newDigits = Array(length).fill('');
      for (let i = 0; i < length; i++) {
        newDigits[i] = pastedData[i] || '';
      }
      setDigits(newDigits);

      const nextEmptyIndex = newDigits.findIndex((d) => d === '');
      if (nextEmptyIndex !== -1) {
        inputRefs.current[nextEmptyIndex]?.focus();
      } else {
        inputRefs.current[length - 1]?.focus();
      }
    }
  }, [length]);

  return (
    <div>
      <div className="flex justify-center gap-2 sm:gap-3">
        {digits.map((digit, index) => (
          <input
            key={index}
            ref={(el) => { inputRefs.current[index] = el; }}
            type="text"
            inputMode="numeric"
            maxLength={1}
            value={digit}
            onChange={(e) => handleDigitChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            disabled={isLoading}
            className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-semibold border ${
              error ? 'border-red-500' : 'border-gray-300'
            } rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#009FE4] focus:border-[#009FE4] disabled:opacity-50 disabled:bg-gray-50`}
            aria-label={`Digit ${index + 1}`}
          />
        ))}
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600 text-center">
          {error}
          {attemptsRemaining !== null && attemptsRemaining > 0 && (
            <span className="block text-xs text-red-500 mt-1">
              {attemptsRemaining} {attemptsRemaining === 1 ? 'attempt' : 'attempts'} remaining
            </span>
          )}
        </p>
      )}
    </div>
  );
});
